import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Package, Calendar, User, MapPin, Phone, Mail, RefreshCw } from 'lucide-react';
import { getAllOrders, updateOrderStatus, requestRefund, Order } from '../services/orderService';
import { notifyOrderStatusChange } from '../services/notificationService';
import { Timestamp } from 'firebase/firestore';

const Container = styled.div`
  background: white;
  border-radius: 10px;
  padding: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }
`;

const Title = styled.h2`
  color: #333;
  font-size: 1.8rem;
  font-weight: 300;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const RefreshButton = styled.button`
  padding: 10px 20px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.3s ease;
  
  &:hover {
    background: #0056b3;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const SearchInput = styled.input`
  padding: 10px 15px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 14px;
  width: 100%;
  max-width: 400px;
  margin-bottom: 20px;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const OrdersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const OrderCard = styled.div`
  border: 2px solid #e1e5e9;
  border-radius: 10px;
  padding: 20px;
  background: #f8f9fa;
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
  flex-wrap: wrap;
  gap: 15px;
`;

const OrderInfo = styled.div`
  flex: 1;
  min-width: 200px;
`;

const OrderNumber = styled.div`
  font-size: 1.3rem;
  font-weight: 600;
  color: #000;
  margin-bottom: 5px;
`;

const OrderMeta = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  font-size: 0.9rem;
  color: #666;
  margin-top: 10px;
`;

const StatusSelect = styled.select`
  padding: 8px 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  min-width: 150px;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const UpdateButton = styled.button`
  padding: 8px 16px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s ease;
  
  &:hover {
    background: #218838;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const CustomerInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 15px;
  padding: 15px;
  background: white;
  border-radius: 8px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: #666;
  
  .label {
    font-weight: 600;
    color: #333;
  }
`;

const ItemsList = styled.div`
  margin-top: 15px;
  padding: 15px;
  background: white;
  border-radius: 8px;
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 15px;
  margin-top: 15px;
  border-top: 2px solid #e1e5e9;
  font-size: 1.2rem;
  font-weight: 600;
  color: #000;
`;

const NoteInput = styled.input`
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.85rem;
  font-family: inherit;
  min-width: 240px;
  flex: 1;

  &:focus {
    outline: none;
    border-color: #1a1a1a;
  }
`;

const RefundButton = styled.button`
  padding: 9px 16px;
  background: white;
  color: #b3242f;
  border: 1px solid #b3242f;
  border-radius: 3px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #b3242f;
    color: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelNotice = styled.div`
  margin-top: 10px;
  padding: 10px 12px;
  background: #fff5f5;
  border-left: 3px solid #dc3545;
  font-size: 0.85rem;
  color: #8c2b34;
`;

const MailHint = styled.div`
  margin-top: 8px;
  font-size: 0.78rem;
  color: #888;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
  
  svg {
    width: 80px;
    height: 80px;
    margin-bottom: 20px;
    opacity: 0.3;
  }
`;

const OrderManagement: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [statusUpdates, setStatusUpdates] = useState<Record<string, Order['status']>>({});
  const [statusNotes, setStatusNotes] = useState<Record<string, string>>({});
  const [refunding, setRefunding] = useState<string | null>(null);

  useEffect(() => {
    loadOrders();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = orders.filter(order =>
        order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customer.surname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customer.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOrders(filtered);
    } else {
      setFilteredOrders(orders);
    }
  }, [searchTerm, orders]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const allOrders = await getAllOrders();
      setOrders(allOrders);
      setFilteredOrders(allOrders);
    } catch (error) {
      console.error('Siparişler yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (orderId: string, newStatus: Order['status']) => {
    setStatusUpdates(prev => ({
      ...prev,
      [orderId]: newStatus,
    }));
  };

  // Siparisi iptal edip parayi iade eder.
  // Gerekce zorunlu; hem kayda gecer hem musteriye giden bildirimde yer alir.
  const handleRefund = async (order: Order) => {
    const reason = (statusNotes[order.id!] || '').trim();

    if (!reason) {
      alert('İade için önce bir gerekçe yazın.');
      return;
    }

    const confirmed = window.confirm(
      `${order.orderNumber} numaralı siparişin ödemesi iptal edilip ` +
      `${order.totalAmount.toLocaleString('tr-TR')} TL müşteriye iade edilecek.\n\n` +
      'Bu işlem geri alınamaz. Onaylıyor musunuz?'
    );
    if (!confirmed) return;

    setRefunding(order.id!);
    const result = await requestRefund(order.id!, order.paymentId, reason);
    setRefunding(null);

    if (!result.success) {
      alert(
        'İade yapılamadı: ' + (result.error || 'bilinmeyen hata') +
        (result.guidance ? '\n\n' + result.guidance : '')
      );
      return;
    }

    setOrders(prev =>
      prev.map(o =>
        o.id === order.id
          ? { ...o, status: 'İptal Edildi' as Order['status'], cancellationReason: reason }
          : o
      )
    );
    setStatusNotes(prev => {
      const next = { ...prev };
      delete next[order.id!];
      return next;
    });

    await notifyOrderStatusChange({
      orderNumber: order.orderNumber,
      customerName: `${order.customer.name} ${order.customer.surname}`.trim(),
      customerEmail: order.customer.email,
      newStatus: 'İptal Edildi',
      note: `${reason} — Ödemeniz iade edilmiştir.`,
    });

    alert(result.message || 'İade işlemi başlatıldı.');
  };

  const handleUpdateStatus = async (orderId: string) => {
    const newStatus = statusUpdates[orderId];
    if (!newStatus || !orderId) return;

    const order = orders.find(o => o.id === orderId);
    if (!order) return;

    const note = (statusNotes[orderId] || '').trim();

    // Iptal icin gerekce zorunlu: musteriye gonderilen bildirimde yer aliyor
    // ve sonradan "neden iptal edildi" sorusuna cevap veriyor.
    if (newStatus === 'İptal Edildi' && !note) {
      alert('İptal işlemi için bir gerekçe yazmalısınız.');
      return;
    }

    setUpdatingStatus(orderId);
    try {
      const result = await updateOrderStatus(orderId, newStatus, note || undefined);

      if (!result.success) {
        alert('Durum güncellenirken hata oluştu: ' + result.error);
        return;
      }

      setOrders(prevOrders =>
        prevOrders.map(o =>
          o.id! === orderId
            ? {
                ...o,
                status: newStatus,
                updatedAt: new Date(),
                ...(newStatus === 'İptal Edildi' && note ? { cancellationReason: note } : {}),
              }
            : o
        )
      );

      setStatusUpdates(prev => {
        const updated = { ...prev };
        delete updated[orderId];
        return updated;
      });
      setStatusNotes(prev => {
        const updated = { ...prev };
        delete updated[orderId];
        return updated;
      });

      // Musteriye bilgilendirme e-postasi. Basarisiz olsa bile durum
      // guncellemesi gecerli kalir, sadece uyari veririz.
      const mailed = await notifyOrderStatusChange({
        orderNumber: order.orderNumber,
        customerName: `${order.customer.name} ${order.customer.surname}`.trim(),
        customerEmail: order.customer.email,
        newStatus,
        note: note || undefined,
      });

      if (!mailed) {
        alert('Durum güncellendi ancak müşteriye bilgilendirme e-postası gönderilemedi.');
      }
    } catch (error: any) {
      alert('Durum güncellenirken hata oluştu: ' + error.message);
    } finally {
      setUpdatingStatus(null);
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingMessage>Yükleniyor...</LoadingMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>
          <Package size={30} />
          Sipariş Yönetimi ({orders.length})
        </Title>
        <RefreshButton onClick={loadOrders} disabled={loading}>
          <RefreshCw size={18} />
          Yenile
        </RefreshButton>
      </Header>

      <SearchInput
        type="text"
        placeholder="Sipariş no, müşteri adı veya e-posta ile ara..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      {filteredOrders.length === 0 ? (
        <EmptyMessage>
          <Package />
          <p>{searchTerm ? 'Arama sonucu bulunamadı' : 'Henüz sipariş yok'}</p>
        </EmptyMessage>
      ) : (
        <OrdersList>
          {filteredOrders.map((order) => (
            <OrderCard key={order.id!}>
              <OrderHeader>
                <OrderInfo>
                  <OrderNumber>{order.orderNumber}</OrderNumber>
                  <OrderMeta>
                    <span>
                      <Calendar size={14} style={{ marginRight: '5px', verticalAlign: 'middle' }} />
                      {(() => {
                        let date: Date;
                        if (order.createdAt instanceof Date) {
                          date = order.createdAt;
                        } else if (order.createdAt instanceof Timestamp) {
                          date = order.createdAt.toDate();
                        } else {
                          date = new Date(order.createdAt);
                        }
                        return date.toLocaleDateString('tr-TR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        });
                      })()}
                    </span>
                    <span>•</span>
                    <span>{order.totalAmount.toFixed(2)} ₺</span>
                  </OrderMeta>
                </OrderInfo>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <StatusSelect
                    value={statusUpdates[order.id!] || order.status}
                    onChange={(e) => handleStatusChange(order.id!, e.target.value as Order['status'])}
                  >
                    <option value="Alındı">Alındı</option>
                    <option value="Hazırlanıyor">Hazırlanıyor</option>
                    <option value="Kargolandı">Kargolandı</option>
                    <option value="Teslim Edildi">Teslim Edildi</option>
                    <option value="İptal Edildi">İptal Edildi</option>
                  </StatusSelect>
                  {(statusUpdates[order.id!] && statusUpdates[order.id!] !== order.status) && (
                    <>
                      <NoteInput
                        value={statusNotes[order.id!] || ''}
                        onChange={(e) =>
                          setStatusNotes(prev => ({ ...prev, [order.id!]: e.target.value }))
                        }
                        placeholder={
                          statusUpdates[order.id!] === 'İptal Edildi'
                            ? 'İptal gerekçesi (zorunlu)'
                            : 'Müşteriye not (isteğe bağlı)'
                        }
                      />
                      <UpdateButton
                        onClick={() => handleUpdateStatus(order.id!)}
                        disabled={updatingStatus === order.id!}
                      >
                        {updatingStatus === order.id! ? 'Güncelleniyor...' : 'Güncelle'}
                      </UpdateButton>
                    </>
                  )}
                </div>
              </OrderHeader>

              {(statusUpdates[order.id!] && statusUpdates[order.id!] !== order.status) && (
                <MailHint>
                  Kaydedince {order.customer.email} adresine bilgilendirme e-postası gönderilir.
                </MailHint>
              )}

              {order.status !== 'İptal Edildi' && (
                <div style={{ marginTop: '10px' }}>
                  <RefundButton
                    onClick={() => handleRefund(order)}
                    disabled={refunding === order.id!}
                  >
                    {refunding === order.id! ? 'İade ediliyor...' : 'İptal Et ve Parayı İade Et'}
                  </RefundButton>
                  <span style={{ fontSize: '0.76rem', color: '#999', marginLeft: '10px' }}>
                    Yukarıdaki not alanına gerekçe yazın
                  </span>
                </div>
              )}

              {order.status === 'İptal Edildi' && order.cancellationReason && (
                <CancelNotice>
                  <strong>İptal gerekçesi:</strong> {order.cancellationReason}
                </CancelNotice>
              )}

              <CustomerInfo>
                <InfoItem>
                  <User size={16} />
                  <span className="label">Müşteri:</span>
                  <span>{order.customer.name} {order.customer.surname}</span>
                </InfoItem>
                <InfoItem>
                  <Phone size={16} />
                  <span>{order.customer.phone}</span>
                </InfoItem>
                <InfoItem>
                  <Mail size={16} />
                  <span>{order.customer.email}</span>
                </InfoItem>
                <InfoItem>
                  <MapPin size={16} />
                  <span>{order.customer.address}, {order.customer.city}</span>
                </InfoItem>
              </CustomerInfo>

              <ItemsList>
                <div style={{ fontWeight: 600, marginBottom: '10px', color: '#333' }}>Sipariş Detayları:</div>
                {order.items.map((item, index) => (
                  <ItemRow key={index}>
                    <div>
                      <div style={{ fontWeight: 600, color: '#333' }}>{item.name}</div>
                      <div style={{ fontSize: '0.85rem', color: '#666' }}>
                        Adet: {item.quantity}
                        {item.size && ` • Beden: ${item.size}`}
                      </div>
                    </div>
                    <div style={{ fontWeight: 600, color: '#000' }}>
                      {(item.price * item.quantity).toFixed(2)} ₺
                    </div>
                  </ItemRow>
                ))}
                <TotalRow>
                  <span>Toplam:</span>
                  <span>{order.totalAmount.toFixed(2)} ₺</span>
                </TotalRow>
              </ItemsList>
            </OrderCard>
          ))}
        </OrdersList>
      )}
    </Container>
  );
};

export default OrderManagement;
